import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ExternalLinkIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { MEMBER_PROFILES } from '../data/member-profiles';
interface MemberProfilePageProps {
  slug: string;
}
export function MemberProfilePage({
  slug
}: MemberProfilePageProps) {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const member = MEMBER_PROFILES.find(m => m.slug === slug);
  if (!member) {
    return <div className="min-h-screen bg-haa-navy flex flex-col items-center justify-center p-6" data-id="element-3425">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center" data-id="element-3426">
          <h1 className="font-bebas text-5xl text-white tracking-wider mb-4" data-id="element-3427">
            {isEs ? 'Miembro No Encontrado' : 'Member Not Found'}
          </h1>
          <p className="font-nunito text-white/70 mb-8" data-id="element-3428">
            {isEs ? 'El perfil que buscas no existe o ha sido movido.' : 'The profile you are looking for does not exist or has been moved.'}
          </p>
          <a href="#" className="inline-flex items-center gap-2 bg-haa-blue text-white font-nunito font-bold px-6 py-3 rounded-full hover:bg-haa-skyblue transition-colors" data-id="element-3429">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-3430" />
            {isEs ? 'Volver al Inicio' : 'Back to Home'}
          </a>
        </motion.div>
      </div>;
  }
  const role = isEs && member.roleEs ? member.roleEs : member.role;
  const badge = isEs ? member.badgeEs : member.badge;
  const bio = isEs ? member.bioEs : member.bio;
  const bioParagraphs = bio.split('\n\n').filter(p => p.trim() !== '');
  return <div className="min-h-screen bg-haa-navy text-white font-nunito" data-id="element-3431">
      {/* Header */}
      <header className="w-full px-6 py-6 sm:px-12 flex items-center justify-between border-b border-white/10" data-id="element-3432">
        <a href="#" className="flex items-center gap-3 group" data-id="element-3433">
          <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-10 w-auto object-contain brightness-0 invert group-hover:opacity-80 transition-opacity" data-id="element-3434" />
        </a>
        <a href="#" className="hidden sm:inline-flex items-center gap-2 text-white/70 hover:text-white font-semibold text-sm transition-colors" data-id="element-3435">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-3436" />
          {isEs ? 'Volver al Inicio' : 'Back to Home'}
        </a>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-12 py-12 lg:py-20" data-id="element-3437">
        {/* Breadcrumb */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="flex items-center gap-2 text-sm text-white/50 mb-12" data-id="element-3438">
          <a href="#" className="hover:text-white transition-colors" data-id="element-3439">
            {isEs ? 'Inicio' : 'Home'}
          </a>
          <span data-id="element-3440">/</span>
          <span className="text-white/80" data-id="element-3441">
            {isEs ? 'Perfil de Miembro' : 'Member Profile'}
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start" data-id="element-3442">
          {/* Photo Column */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5" data-id="element-3443">
            <div className="aspect-square w-full rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-white/5" data-id="element-3444">
              <img src={member.photo} alt={member.name} className="w-full h-full object-cover object-top" data-id="element-3445" />
            </div>
          </motion.div>

          {/* Content Column */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7 flex flex-col" data-id="element-3446">
            <div className="mb-8" data-id="element-3447">
              <span className="inline-block px-3 py-1 bg-white/10 border border-white/20 rounded-md text-xs font-bold tracking-widest uppercase text-white/90 mb-4" data-id="element-3448">
                {badge}
              </span>
              <h1 className="font-bebas text-6xl sm:text-7xl lg:text-8xl tracking-wider leading-none mb-2 text-white" data-id="element-3449">
                {member.name}
              </h1>
              <p className="text-xl sm:text-2xl text-haa-skyblue font-semibold" data-id="element-3450">
                {role}
              </p>
            </div>

            <div className="space-y-6 text-white/80 text-lg leading-relaxed mb-10" data-id="element-3451">
              {bioParagraphs.map((paragraph, idx) => <p key={idx} data-id="element-3452">{paragraph}</p>)}
            </div>

            {member.socialLinks && member.socialLinks.length > 0 && <div className="pt-8 border-t border-white/10" data-id="element-3453">
                <h3 className="text-sm font-bold uppercase tracking-wider text-white/50 mb-4" data-id="element-3454">
                  {isEs ? 'Conectar' : 'Connect'}
                </h3>
                <div className="flex flex-wrap gap-4" data-id="element-3455">
                  {member.socialLinks.map((link, idx) => <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm transition-colors border border-white/10" data-id="element-3456">
                      {link.label}
                      <ExternalLinkIcon className="w-3.5 h-3.5 opacity-70" data-id="element-3457" />
                    </a>)}
                </div>
              </div>}
          </motion.div>
        </div>

        {/* Mobile Back Button */}
        <div className="mt-16 sm:hidden" data-id="element-3458">
          <a href="#" className="flex items-center justify-center gap-2 w-full bg-white/10 text-white font-nunito font-bold px-6 py-4 rounded-xl hover:bg-white/20 transition-colors" data-id="element-3459">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-3460" />
            {isEs ? 'Volver al Inicio' : 'Back to Home'}
          </a>
        </div>
      </main>
    </div>;
}